import { Box } from "@mui/material";
import { GridSortItem } from "@mui/x-data-grid";
import { Dispatch, SetStateAction, useState } from "react";
import { useOutletContext } from "react-router-dom";
import CustomDataGridV2 from "../../../../../../components/CustomDataGridV2";
import { GlobalTableHeightWrapper } from "../../../../../../components/GlobalTableHeightWrapper";
import Text from "../../../../../../components/Text";
import { usePatientAccountReceivable } from "../../../../../../queries/account-receivables/use-patient-account-receivable";
import { useAccountReceivableTotal } from "../../../../../../queries/account-receivables/use-account-receivable-totals";
import {
  applyDefaultColumnSettings,
  DEF_PAGINATION,
  DGT_Pagination,
  getXtenantId,
} from "../../../../../../utils/utils";
import {
  AccountReceivableFilterType,
  formatCurrency,
  toSafeIsoString,
  UPPER_TABLE_COLUMNS,
} from "../account-receivable-constant";

type AccountReceivableOutletContext = {
  search: string;
  setSearch: (search: string) => void;
  filter: AccountReceivableFilterType;
  setFilter: Dispatch<SetStateAction<AccountReceivableFilterType>>;
};

export type PatientAccountReceivableRow = {
  patientId?: string;
  patientName?: string;
  gender?: string;
  dob?: string;
  duration0To30?: number;
  duration31To60?: number;
  duration61To90?: number;
  duration91To120?: number;
  duration120Plus?: number;
  total?: number;
};

const AccountReceivablePatientTable = () => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { search, filter } = useOutletContext<AccountReceivableOutletContext>();

  const startDate = toSafeIsoString(filter?.startDate);
  const endDate = toSafeIsoString(filter?.endDate);
  const searchString = search || undefined;

  const { data: listResponse, isLoading: isListLoading } =
    usePatientAccountReceivable({
      pagination,
      gridSorting,
      payerType: "PATIENT",
      searchString,
      startDate,
      endDate,
      xTENANTID: getXtenantId(),
    });

  const { data: totalsResponse, isLoading: isTotalsLoading } =
    useAccountReceivableTotal({
      searchString,
      startDate,
      endDate,
      payerType: "PATIENT",
      xTENANTID: getXtenantId(),
    });

  const COLUMNS = applyDefaultColumnSettings<PatientAccountReceivableRow>(
    [
      {
        field: "patientId",
        flex: 0.5,
        minWidth: 100,
        headerName: "Patient ID",
        renderCell: ({ value }) => <Text isTabletext>{value ?? "-"}</Text>,
      },
      {
        field: "patientName",
        flex: 1,
        minWidth: 200,
        headerName: "Patient Name",
        renderCell: ({ value }) => <Text isTabletext>{value ?? "-"}</Text>,
      },
      {
        field: "duration0To30",
        flex: 0.5,
        minWidth: 110,
        headerName: "0-30 Days",
        renderCell: ({ value }) => (
          <Text isTabletext>{formatCurrency(value)}</Text>
        ),
      },
      {
        field: "duration31To60",
        flex: 0.5,
        minWidth: 110,
        headerName: "31-60 Days",
        renderCell: ({ value }) => (
          <Text isTabletext>{formatCurrency(value)}</Text>
        ),
      },
      {
        field: "duration61To90",
        flex: 0.5,
        minWidth: 110,
        headerName: "61-90 Days",
        renderCell: ({ value }) => (
          <Text isTabletext>{formatCurrency(value)}</Text>
        ),
      },
      {
        field: "duration91To120",
        flex: 0.5,
        minWidth: 110,
        headerName: "91-120 Days",
        renderCell: ({ value }) => (
          <Text isTabletext>{formatCurrency(value)}</Text>
        ),
      },
      {
        field: "duration120Plus",
        flex: 0.5,
        minWidth: 110,
        headerName: "120+ Days",
        renderCell: ({ value }) => (
          <Text isTabletext>{formatCurrency(value)}</Text>
        ),
      },
      {
        field: "total",
        flex: 0.5,
        minWidth: 100,
        headerName: "Total",
        renderCell: ({ value }) => (
          <Text isTabletext>{formatCurrency(value)}</Text>
        ),
      },
    ],
    ["patientName", "total"]
  );

  const topColumnHeight = 80;

  return (
    <Box>
      <Box height={`${topColumnHeight}px`}>
        <CustomDataGridV2
          rows={totalsResponse?.data ? [totalsResponse.data] : []}
          hideFooter
          rowCount={totalsResponse?.data ? 1 : 0}
          getRowId={() => "totals"}
          rowHeight={40}
          columns={UPPER_TABLE_COLUMNS}
          loading={isTotalsLoading}
        />
      </Box>

      <GlobalTableHeightWrapper
        height={`calc(100vh - ${topColumnHeight + 168}px)`}
      >
        <CustomDataGridV2
          searchString={searchString}
          loading={isListLoading}
          rows={listResponse?.data?.content ?? []}
          getRowId={(row) => row?.patientId ?? ""}
          columns={COLUMNS}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          onSortModelChange={setGridSorting}
          rowCount={listResponse?.data.page.totalElements}
          disableRowSelectionOnClick
        />
      </GlobalTableHeightWrapper>
    </Box>
  );
};

export default AccountReceivablePatientTable;

export const getTotalCell = (value?: number) =>
  getBoldText(value || value === 0 ? `$${value}` : "-");

export const getBoldText = (value: string) => (
  <Text fontWeight={"bold"} fontSize={"20px"}>
    {value}
  </Text>
);
